import { Typography, TextField, MenuItem, Button, IconButton, Chip } from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import AddIcon from '@material-ui/icons/Add';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  RULE_KINDS, RULE_MODE_OPTIONS, TRANSFORM_OPS, EXPR_OPS,
  detectRuleKind, ruleForKind, emptyTransform,
} from './ruleUtils';

// Renderer for a source-column option: shows the column name with the sample file's first-row value
// beside it (so the user can see what the data looks like), and strikes/greys columns already used in
// a mapping — still selectable (a source can feed more than one target), just flagged as taken.
export function makeSourceRenderOption(sampleFirstRow = {}, usedSources) {
  const used = usedSources || new Set();
  return (option) => {
    const raw = sampleFirstRow[option];
    const sample = raw == null ? '' : String(raw);
    const isUsed = used.has(option);
    return (
      <span style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 8, alignItems: 'baseline' }}>
        <span style={{ textDecoration: isUsed ? 'line-through' : 'none', color: isUsed ? '#9e9e9e' : 'inherit' }}>{option}</span>
        {sample !== '' ? (
          <span style={{ color: '#9e9e9e', fontSize: 12, maxWidth: '55%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sample}</span>
        ) : null}
      </span>
    );
  };
}

// Parse/format the map{table} object as `key=value` lines — compact enough for an inline editor.
const tableToText = (table) => Object.entries(table || {}).map(([k, v]) => `${k}=${v}`).join('\n');
const textToTable = (text) => {
  const t = {};
  String(text).split('\n').forEach((line) => {
    const i = line.indexOf('=');
    if (i > 0) t[line.slice(0, i).trim()] = line.slice(i + 1).trim();
  });
  return t;
};

// Editor for a single transform step's params.
function TransformStep({ step, onChange, onRemove }) {
  const meta = TRANSFORM_OPS.find((o) => o.op === step.op);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
      <TextField
        select size="small" label="Step" value={step.op} style={{ minWidth: 130 }}
        onChange={(e) => onChange(emptyTransform(e.target.value))}
      >
        {TRANSFORM_OPS.map((o) => <MenuItem key={o.op} value={o.op}>{o.label}</MenuItem>)}
      </TextField>
      {meta?.params.includes('start') && (
        <TextField size="small" type="number" label="Start" value={step.start ?? ''} style={{ width: 80 }}
          onChange={(e) => onChange({ ...step, start: e.target.value === '' ? 0 : Number(e.target.value) })} />
      )}
      {meta?.params.includes('end') && (
        <TextField size="small" type="number" label="End" value={step.end ?? ''} style={{ width: 80 }}
          onChange={(e) => onChange({ ...step, end: e.target.value === '' ? 0 : Number(e.target.value) })} />
      )}
      {meta?.params.includes('find') && (
        <TextField size="small" label="Find" value={step.find ?? ''} style={{ width: 110 }}
          onChange={(e) => onChange({ ...step, find: e.target.value })} />
      )}
      {meta?.params.includes('with') && (
        <TextField size="small" label="With" value={step.with ?? ''} style={{ width: 110 }}
          onChange={(e) => onChange({ ...step, with: e.target.value })} />
      )}
      {meta?.params.includes('table') && (
        <TextField size="small" label="key=value per line" multiline minRows={2} value={tableToText(step.table)} style={{ width: 200 }}
          onChange={(e) => onChange({ ...step, table: textToTable(e.target.value) })} />
      )}
      <IconButton size="small" onClick={onRemove} aria-label="remove step"><DeleteOutlineIcon fontSize="small" /></IconButton>
    </div>
  );
}

// The ordered transform pipeline for a from/concat rule (the "middle" transforms).
function TransformPipeline({ transforms, onChange }) {
  const list = Array.isArray(transforms) ? transforms : [];
  const set = (i, step) => onChange(list.map((s, j) => (j === i ? step : s)));
  const remove = (i) => onChange(list.filter((_, j) => j !== i));
  const add = () => onChange([...list, emptyTransform('trim')]);
  return (
    <div style={{ marginTop: 8 }}>
      <Typography variant="caption" color="textSecondary">Transform steps (applied in order)</Typography>
      {list.map((s, i) => <TransformStep key={i} step={s} onChange={(step) => set(i, step)} onRemove={() => remove(i)} />)}
      <Button size="small" startIcon={<AddIcon />} onClick={add}>Add step</Button>
    </div>
  );
}

// Editor for a single expr operand ({col} or {const}).
function ExprOperand({ operand, sourceHeaders, renderOption, onChange, onRemove }) {
  const isCol = operand && 'col' in operand;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <TextField select size="small" value={isCol ? 'col' : 'const'} style={{ width: 110 }}
        onChange={(e) => onChange(e.target.value === 'col' ? { col: '' } : { const: '' })}
      >
        <MenuItem value="col">Column</MenuItem>
        <MenuItem value="const">Number</MenuItem>
      </TextField>
      {isCol ? (
        <Autocomplete
          freeSolo options={sourceHeaders} value={operand.col || ''} style={{ width: 200 }} renderOption={renderOption}
          onChange={(_, v) => onChange({ col: v || '' })}
          onInputChange={(_, v) => onChange({ col: v || '' })}
          renderInput={(p) => <TextField {...p} size="small" label="Source column" />}
        />
      ) : (
        <TextField size="small" label="Value" value={operand.const ?? ''} style={{ width: 200 }}
          onChange={(e) => onChange({ const: e.target.value })} />
      )}
      <IconButton size="small" onClick={onRemove} aria-label="remove operand"><DeleteOutlineIcon fontSize="small" /></IconButton>
    </div>
  );
}

/**
 * Middle-column editor for the currently selected target column. Reads the field's rule out of the
 * active output template and writes it back via onChange(newRule) (undefined = unmapped).
 */
export default function SlotEditor({ targetKey, targetLabel, rule, sourceHeaders, sampleFirstRow, usedSources, onChange }) {
  if (!targetKey) {
    return <Typography variant="body2" color="textSecondary">Select a showroom-pricing column on the left to map it.</Typography>;
  }
  const kind = detectRuleKind(rule);
  const opts = sourceHeaders || [];
  const renderOpt = makeSourceRenderOption(sampleFirstRow, usedSources);

  const setKind = (k) => onChange(ruleForKind(k, rule));

  return (
    <div>
      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{targetLabel || targetKey}</Typography>
      <Typography variant="caption" color="textSecondary" gutterBottom style={{ display: 'block' }}>{targetKey}</Typography>

      <TextField
        select size="small" fullWidth label="Mapping type" style={{ marginTop: 8 }}
        value={kind === RULE_KINDS.ADVANCED ? RULE_KINDS.ADVANCED : kind}
        onChange={(e) => setKind(e.target.value)}
      >
        {RULE_MODE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        {kind === RULE_KINDS.ADVANCED && <MenuItem value={RULE_KINDS.ADVANCED} disabled>Advanced (JSON only)</MenuItem>}
      </TextField>

      {kind === RULE_KINDS.ADVANCED && (
        <Typography variant="caption" style={{ color: '#b26a00', display: 'block', marginTop: 10 }}>
          This column uses an advanced rule (conditional / nested expression). Edit it in the Advanced (JSON) view;
          it is preserved as-is here.
        </Typography>
      )}

      {kind === RULE_KINDS.FROM && (
        <div style={{ marginTop: 10 }}>
          <Autocomplete
            freeSolo options={opts} value={rule.from || ''} renderOption={renderOpt}
            onChange={(_, v) => onChange({ ...rule, from: v || '' })}
            onInputChange={(_, v) => onChange({ ...rule, from: v || '' })}
            renderInput={(p) => <TextField {...p} size="small" label="Source column" fullWidth />}
          />
          <TransformPipeline transforms={rule.transform} onChange={(t) => onChange({ ...rule, transform: t })} />
          <TextField size="small" label="Default if blank (optional)" value={rule.default ?? ''} style={{ marginTop: 8, width: 240 }}
            onChange={(e) => onChange({ ...rule, default: e.target.value === '' ? undefined : e.target.value })} />
        </div>
      )}

      {kind === RULE_KINDS.CONST && (
        <TextField size="small" fullWidth label="Constant value" value={rule.const ?? ''} style={{ marginTop: 10 }}
          onChange={(e) => onChange({ const: e.target.value })} />
      )}

      {kind === RULE_KINDS.CONCAT && (
        <div style={{ marginTop: 10 }}>
          <Typography variant="caption" color="textSecondary">Columns to join</Typography>
          {(rule.concat || []).map((c, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <Autocomplete
                freeSolo options={opts} value={c || ''} style={{ width: 240 }} renderOption={renderOpt}
                onChange={(_, v) => onChange({ ...rule, concat: rule.concat.map((x, j) => (j === i ? (v || '') : x)) })}
                onInputChange={(_, v) => onChange({ ...rule, concat: rule.concat.map((x, j) => (j === i ? (v || '') : x)) })}
                renderInput={(p) => <TextField {...p} size="small" label={`Column ${i + 1}`} />}
              />
              <IconButton size="small" aria-label="remove column"
                onClick={() => onChange({ ...rule, concat: rule.concat.filter((_, j) => j !== i) })}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </div>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={() => onChange({ ...rule, concat: [...(rule.concat || []), ''] })}>Add column</Button>
          <TextField size="small" label="Delimiter" value={rule.delimiter ?? ''} style={{ marginLeft: 12, width: 120 }}
            onChange={(e) => onChange({ ...rule, delimiter: e.target.value })} />
          <TransformPipeline transforms={rule.transform} onChange={(t) => onChange({ ...rule, transform: t })} />
        </div>
      )}

      {kind === RULE_KINDS.TODAY && (
        <TextField size="small" label="Date format" value={rule.today ?? ''} style={{ marginTop: 10, width: 200 }}
          helperText="e.g. YYYY-MM-DD"
          onChange={(e) => onChange({ today: e.target.value })} />
      )}

      {kind === RULE_KINDS.EXPR && (
        <div style={{ marginTop: 10 }}>
          <TextField select size="small" label="Operation" style={{ width: 200 }}
            value={Object.keys(rule.expr).find((k) => EXPR_OPS.some((o) => o.op === k)) || 'mul'}
            onChange={(e) => {
              const opKey = Object.keys(rule.expr).find((k) => EXPR_OPS.some((o) => o.op === k)) || 'mul';
              onChange({ expr: { [e.target.value]: rule.expr[opKey] || [] } });
            }}
          >
            {EXPR_OPS.map((o) => <MenuItem key={o.op} value={o.op}>{o.label}</MenuItem>)}
          </TextField>
          {(() => {
            const opKey = Object.keys(rule.expr).find((k) => EXPR_OPS.some((o) => o.op === k)) || 'mul';
            const operands = rule.expr[opKey] || [];
            const setOperands = (next) => onChange({ expr: { [opKey]: next } });
            return (
              <div style={{ marginTop: 8 }}>
                {operands.map((o, i) => (
                  <ExprOperand key={i} operand={o} sourceHeaders={opts} renderOption={renderOpt}
                    onChange={(v) => setOperands(operands.map((x, j) => (j === i ? v : x)))}
                    onRemove={() => setOperands(operands.filter((_, j) => j !== i))} />
                ))}
                <Button size="small" startIcon={<AddIcon />} onClick={() => setOperands([...operands, { col: '' }])}>Add operand</Button>
              </div>
            );
          })()}
        </div>
      )}

      {kind !== RULE_KINDS.UNMAPPED && kind !== RULE_KINDS.ADVANCED && (
        <div style={{ marginTop: 12 }}>
          <Chip size="small" label="Clear mapping" onClick={() => onChange(undefined)} variant="outlined" />
        </div>
      )}
    </div>
  );
}
